import { Role } from "../../shared/utils/role.model";
import { APP_PATHS } from "../../shared/utils/app-route-paths";
import { MERCHANT_DASHBOARD_ROLES } from "../../shared/utils/merchant-dashboard-roles";

export interface HeaderToolbarLink {
  id: string;
  labelKey: string;
  path: string;
  icon: string;
  /** Official brand mark (Font Awesome) instead of sidebar stroke icon. */
  brandIcon?: "meta" | "whatsapp";
  authorities?: string[];
  /** When multiple authorities are listed: "any" (default) or "all". */
  roleMode?: "any" | "all";
  groups?: string[];
  hideForActivity?: string;
  activityCondition?: string;
  /** Hide this link when the user already has the given role (e.g. meta vs full settings). */
  excludeIfRole?: string;
}

export interface HeaderSetupStep {
  id: string;
  labelKey: string;
  descriptionKey: string;
  path: string;
  pathPrefix: string;
  authorities?: string[];
  roleMode?: "any" | "all";
  groups?: string[];
  excludeIfRole?: string;
  /** Hide this step for a company activity (e.g. catalog for RESERVATION_HOSTELS — rooms, not products). */
  hideForActivity?: string;
}

export const HEADER_QUICK_APPS: HeaderToolbarLink[] = [
  {
    id: "inbox",
    labelKey: "MENU.INBOX",
    path: APP_PATHS.messaging.inbox,
    icon: "inbox",
    authorities: [Role.TEAM_INBOX, Role.CONVERSATION_VIEW],
    groups: ["/MESSAGING"],
  },
  {
    id: "contacts",
    labelKey: "MENU.CONTACTS",
    path: APP_PATHS.messaging.contacts,
    icon: "address-book",
    authorities: [Role.MANAGE_CONTACT, Role.CONTACT_VIEW],
    groups: ["/MESSAGING"],
  },
  {
    id: "templates",
    labelKey: "MENU.TEMPLATES",
    path: APP_PATHS.workflow.templates,
    icon: "clipboard",
    authorities: [
      Role.MANAGE_TEMPLATE,
      Role.MANAGE_BOTS,
      Role.MANAGE_FLOW,
      Role.WORKFLOW_VIEW,
    ],
    groups: ["/MESSAGING"],
  },
  {
    id: "campaigns",
    labelKey: "MENU.CAMPAIGNS",
    path: APP_PATHS.campaigns.history,
    icon: "megaphone",
    authorities: [Role.BROADCAST_HISTORY, Role.BROADCAST_ADD, Role.BROADCAST_VIEW],
    groups: ["/MESSAGING"],
  },
  {
    id: "automations",
    labelKey: "MENU.AUTOMATION",
    path: APP_PATHS.workflow.bots,
    icon: "sparkles",
    authorities: [Role.AUTOMATION_DEF_ACTIONS, Role.AUTOMATION, Role.WORKFLOW_VIEW],
    groups: ["/MESSAGING"],
  },
  {
    id: "analytics",
    labelKey: "MENU.DASHBOARD",
    path: APP_PATHS.dashboard,
    icon: "layout-dashboard",
    // Same audience as the /dashboard route: any role of a dashboard section, no group gate.
    authorities: [...MERCHANT_DASHBOARD_ROLES],
    roleMode: "any",
  },
  {
    id: "orders",
    labelKey: "MENU.ORDERS",
    path: APP_PATHS.catalog.orders,
    icon: "shopping-cart",
    authorities: [Role.ORDERS, Role.ORDERS_VIEW],
    groups: ["/ORDER"],
    hideForActivity: "RESERVATION_HOSTELS",
  },
  {
    id: "reservations",
    labelKey: "MENU.RESERVATIONS",
    path: APP_PATHS.catalog.reservations,
    icon: "calendar",
    authorities: [Role.ORDERS, Role.ORDERS_VIEW],
    groups: ["/ORDER"],
    activityCondition: "RESERVATION_HOSTELS",
  },
  {
    id: "tickets",
    labelKey: "MENU.TICKETS",
    path: APP_PATHS.messaging.tickets,
    icon: "ticket",
    authorities: [Role.MANAGE_TICKET, Role.TICKET_VIEW],
    groups: ["/SUPPORT"],
  },
  {
    id: "platform",
    labelKey: "MENU.PLATFORM_COMPANIES",
    path: APP_PATHS.platform.companies,
    icon: "building",
    authorities: [Role.DASHY_COMPANIES, Role.ADMIN_SOLUTION_UI],
    groups: ["/SOLUTION"],
  },
];

export const HEADER_QUICK_CREATE: HeaderToolbarLink[] = [
  {
    id: "campaign",
    labelKey: "HEADER.TOOLBAR.NEW_CAMPAIGN",
    path: APP_PATHS.campaigns.new,
    icon: "megaphone",
    authorities: [Role.BROADCAST_ADD],
  },
  {
    id: "contact-upload",
    labelKey: "MENU.CONTACT.UPLOAD",
    path: APP_PATHS.messaging.contactImport,
    icon: "upload",
    authorities: [Role.MANAGE_CONTACT],
  },
  {
    id: "template",
    labelKey: "MENU.TEMPLATE.LIST",
    path: APP_PATHS.workflow.templates,
    icon: "clipboard",
    authorities: [Role.MANAGE_TEMPLATE],
  },
  {
    id: "flow",
    labelKey: "MENU.TEMPLATE.FLOW",
    path: APP_PATHS.workflow.forms,
    icon: "workflow",
    authorities: [Role.MANAGE_FLOW],
  },
  {
    id: "ticket",
    labelKey: "HEADER.TOOLBAR.NEW_TICKET",
    path: APP_PATHS.messaging.tickets,
    icon: "ticket",
    authorities: [Role.MANAGE_TICKET],
  },
];

export const HEADER_PLATFORM_MENU: HeaderToolbarLink[] = [
  {
    id: "platform-companies",
    labelKey: "MENU.PLATFORM_COMPANIES",
    path: APP_PATHS.platform.companies,
    icon: "building",
    authorities: [Role.DASHY_COMPANIES, Role.ADMIN_SOLUTION_UI],
  },
  {
    id: "platform-dashboard",
    labelKey: "MENU.PLATFORM_DASHBOARD",
    path: APP_PATHS.platform.dashboard,
    icon: "bar-chart",
    authorities: [Role.ADMIN_SOLUTION_UI],
  },
  {
    id: "platform-usage",
    labelKey: "MENU.PLATFORM_USAGE",
    path: APP_PATHS.platform.usage,
    icon: "layers",
    authorities: [Role.ADMIN_SOLUTION_UI],
  },
  {
    id: "platform-billing",
    labelKey: "MENU.PLATFORM_BILLING",
    path: APP_PATHS.platform.billing,
    icon: "credit-card",
    authorities: [Role.ADMIN_SOLUTION_UI],
  },
];

/** Settings module parent route accepts either administration or order group. */
const SETTINGS_GROUPS = ["/ADMINISTRATION", "/ORDER"] as const;

export const HEADER_COMPANY_MENU: HeaderToolbarLink[] = [
  {
    id: "company-settings",
    labelKey: "MENU.SETTINGS_COMPANY_PARAMS",
    path: APP_PATHS.settings.company,
    icon: "sliders",
    authorities: [Role.COMPANY_MANAGE],
    groups: [...SETTINGS_GROUPS],
  },
  {
    id: "plan",
    labelKey: "MENU.SETTINGS_PLAN",
    path: APP_PATHS.settings.plan,
    icon: "credit-card",
    authorities: [Role.COMPANY_MANAGE],
    groups: [...SETTINGS_GROUPS],
  },
  {
    id: "team-settings",
    labelKey: "MENU.SETTINGS_TEAM_PARAMS",
    path: APP_PATHS.settings.agents,
    icon: "users",
    authorities: [Role.MANAGE_AGENT, Role.MANAGE_AGENT_GROUP],
    groups: [...SETTINGS_GROUPS],
  },
  {
    id: "meta-subscription",
    labelKey: "MENU.ADMINISTRATION.META",
    path: `${APP_PATHS.settings.company}?section=meta&view=connection`,
    icon: "meta",
    brandIcon: "meta",
    authorities: [Role.CATALOG_SUBSCRIPTION],
    groups: [...SETTINGS_GROUPS],
    excludeIfRole: Role.COMPANY_MANAGE,
  },
  {
    id: "catalog",
    labelKey: "MENU.PRODUCTS",
    path: APP_PATHS.catalog.products,
    icon: "package",
    authorities: [Role.PRODUCT, Role.PRODUCT_VIEW],
    groups: ["/ORDER"],
    hideForActivity: "RESERVATION_HOSTELS",
  },
  {
    id: "audit",
    labelKey: "MENU.SETTINGS_AUDIT",
    path: APP_PATHS.settings.audit,
    icon: "newspaper",
    authorities: [Role.AUDIT_MANAGE],
    groups: [...SETTINGS_GROUPS],
  },
  {
    id: "notifications-settings",
    labelKey: "MENU.AUTOMATIONS.NOTIFICATION",
    path: APP_PATHS.settings.notifications,
    icon: "bell",
    authorities: [Role.AUDIT_MANAGE],
    groups: [...SETTINGS_GROUPS],
  },
];

/**
 * The steps a company truly needs to send AND receive WhatsApp messages:
 * a connected number (Meta Subscription), the webhook subscription that
 * actually lets messages reach Dashy, and an approved-ready template. All
 * three are marked done from real account data (see header.component.ts),
 * not just from visiting the page — see isSetupStepDone().
 */
export const HEADER_SETUP_STEPS: HeaderSetupStep[] = [
  {
    id: "meta",
    labelKey: "HEADER.SETUP.META",
    descriptionKey: "HEADER.SETUP.META_DESC",
    // NOTE: GeneralParamsPageComponent still reads `section`/`meta` query
    // params as `gp-section-subscription`/`connection` — not the `section=meta&view=connection`
    // shape used elsewhere in APP_PATHS-based links. Keep these values in
    // sync with gp-sections.ts until that page adopts the new query scheme.
    path: `${APP_PATHS.settings.company}?section=gp-section-subscription&meta=connection`,
    pathPrefix: APP_PATHS.settings.company,
    authorities: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
    groups: [...SETTINGS_GROUPS],
  },
  {
    id: "messaging",
    labelKey: "HEADER.SETUP.MESSAGING",
    descriptionKey: "HEADER.SETUP.MESSAGING_DESC",
    path: `${APP_PATHS.settings.company}?section=gp-section-whatsapp`,
    pathPrefix: APP_PATHS.settings.company,
    authorities: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
    groups: [...SETTINGS_GROUPS],
  },
  {
    id: "templates",
    labelKey: "HEADER.SETUP.TEMPLATES",
    descriptionKey: "HEADER.SETUP.TEMPLATES_DESC",
    path: APP_PATHS.workflow.templateRequestNew,
    pathPrefix: APP_PATHS.workflow.templates,
    authorities: [Role.MANAGE_TEMPLATE],
    groups: ["/MESSAGING"],
  },
];

/**
 * "Mon restaurant en 5 étapes": what a RESTAURANT needs before its first real order. Shown first for a
 * restaurant only, and each one is checked against real data (setup-checklist.service.ts), never by a visit.
 */
export const RESTAURANT_SETUP_STEPS: HeaderSetupStep[] = [
  {
    id: "restaurant-dishes",
    labelKey: "HEADER.SETUP.RESTAURANT_DISHES",
    descriptionKey: "HEADER.SETUP.RESTAURANT_DISHES_DESC",
    path: APP_PATHS.catalog.productNew,
    pathPrefix: APP_PATHS.catalog.products,
    groups: ["/ORDER"],
  },
  {
    id: "restaurant-map",
    labelKey: "HEADER.SETUP.RESTAURANT_MAP",
    descriptionKey: "HEADER.SETUP.RESTAURANT_MAP_DESC",
    path: `${APP_PATHS.catalog.whatsappStorefront}?tab=salePoints`,
    pathPrefix: APP_PATHS.catalog.whatsappStorefront,
    groups: ["/ORDER"],
  },
  {
    id: "restaurant-zones",
    labelKey: "HEADER.SETUP.RESTAURANT_ZONES",
    descriptionKey: "HEADER.SETUP.RESTAURANT_ZONES_DESC",
    path: `${APP_PATHS.catalog.orders}?tab=delivery`,
    pathPrefix: APP_PATHS.catalog.orders,
    groups: ["/ORDER"],
  },
  {
    id: "restaurant-tables",
    labelKey: "HEADER.SETUP.RESTAURANT_TABLES",
    descriptionKey: "HEADER.SETUP.RESTAURANT_TABLES_DESC",
    path: APP_PATHS.catalog.diningTables,
    pathPrefix: APP_PATHS.catalog.diningTables,
    groups: ["/ORDER"],
  },
  {
    id: "restaurant-test-order",
    labelKey: "HEADER.SETUP.RESTAURANT_TEST_ORDER",
    descriptionKey: "HEADER.SETUP.RESTAURANT_TEST_ORDER_DESC",
    path: APP_PATHS.catalog.whatsappStorefront,
    pathPrefix: APP_PATHS.catalog.orders,
    groups: ["/ORDER"],
  },
];

/** "Discover" steps — not required, not counted in the progress meter. */
export const HEADER_OPTIONAL_STEPS: HeaderSetupStep[] = [
  {
    id: "general",
    labelKey: "HEADER.SETUP.GENERAL",
    descriptionKey: "HEADER.SETUP.GENERAL_DESC",
    path: APP_PATHS.settings.company,
    pathPrefix: APP_PATHS.settings.company,
    authorities: [Role.COMPANY_MANAGE],
    groups: [...SETTINGS_GROUPS],
  },
  {
    id: "catalog",
    labelKey: "HEADER.SETUP.CATALOG",
    descriptionKey: "HEADER.SETUP.CATALOG_DESC",
    path: `${APP_PATHS.settings.company}?section=gp-section-subscription&meta=catalog`,
    pathPrefix: APP_PATHS.settings.company,
    authorities: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
    groups: [...SETTINGS_GROUPS],
    // A hotel sells rooms ("chambres"), not products — no Meta product catalog to link.
    hideForActivity: "RESERVATION_HOSTELS",
  },
  {
    id: "campaign",
    labelKey: "HEADER.SETUP.CAMPAIGN",
    descriptionKey: "HEADER.SETUP.CAMPAIGN_DESC",
    path: APP_PATHS.campaigns.new,
    pathPrefix: "/campaigns",
    authorities: [Role.BROADCAST_ADD],
    groups: ["/MESSAGING"],
  },
  {
    id: "ai-agent",
    labelKey: "HEADER.SETUP.AI_AGENT",
    descriptionKey: "HEADER.SETUP.AI_AGENT_DESC",
    path: APP_PATHS.workflow.aiAgents,
    pathPrefix: APP_PATHS.workflow.aiAgents,
    authorities: [Role.AUTOMATION],
    groups: ["/MESSAGING"],
  },
];

export const SETUP_STORAGE_KEY = "dashy_setup_progress";
